import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { authApi } from '@/services/Auth/AuthService'
import { operationApi } from '@/services/Operation/OperationService'
import type { AppDispatch, RootState } from '@/store'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  AUTH_STORAGE_KEYS,
  get,
  setAuthStorage,
} from '@/utils/storage/adapters/localStorageAdapter'

import {
  getQueuedMutations,
  isPermanentMutationError,
  MUTATION_QUEUE_CHANGED_EVENT,
  removeQueuedMutation,
  scheduleQueuedMutationRetry,
} from './mutationQueue'

const RETRY_BASE_DELAY_MS = 5000
const RETRY_MAX_DELAY_MS = 60_000
const MUTATION_QUEUE_LOCK = 'tcsofor-mutation-queue'

const getRetryDelay = (attempts: number) =>
  Math.min(RETRY_BASE_DELAY_MS * 2 ** attempts, RETRY_MAX_DELAY_MS)

const runWithMutationQueueLock = async (callback: () => Promise<void>): Promise<void> => {
  if (!('locks' in navigator)) {
    await callback()
    return
  }
  await navigator.locks.request(MUTATION_QUEUE_LOCK, callback)
}

const isAccessTokenExpired = (): boolean => {
  const expiresAt = get(AUTH_STORAGE_KEYS.tokenExpiresAt)
  if (!expiresAt) return true
  const time = new Date(expiresAt).getTime()
  return Number.isNaN(time) || Date.now() >= time - 5000
}

// Refresh proactively before replaying. A driver is offline far longer than the
// 5-minute access-token lifetime, so the queued token is almost always expired.
// We can't lean on the 401-then-refresh path: cross-origin the backend's 401 has no
// CORS headers, so the browser masks it as a network error and the reauth never fires.
const ensureFreshToken = async (dispatch: AppDispatch): Promise<void> => {
  if (!isAccessTokenExpired()) return
  const refreshToken = get(AUTH_STORAGE_KEYS.refreshToken)
  if (!refreshToken) return

  const data = await dispatch(authApi.endpoints.refreshToken.initiate({ refreshToken })).unwrap()
  setAuthStorage({
    token: data.accessToken.token,
    tokenExpiresAt: data.accessTokenExpires,
    refreshToken: data.refreshToken,
    refreshTokenExpiresAt: data.refreshTokenExpires,
  })
}

// Replays queued offline mutations once connectivity returns, refreshing the access
// token first so a request queued hours ago still goes through. Runs on reconnect
// and on app start.
export const useMutationQueueSync = (): void => {
  const dispatch = useAppDispatch()
  const ownerId = useAppSelector((state: RootState) => state.driverSlice.phoneNumber)
  const { t } = useTranslation('', { keyPrefix: 'offline' })

  useEffect(() => {
    let draining = false
    let retryTimer: number | undefined

    async function drain() {
      if (draining || !navigator.onLine || !ownerId) return
      draining = true
      let retryDelayToSchedule: number | undefined
      try {
        await runWithMutationQueueLock(async () => {
          const records = await getQueuedMutations(ownerId)
          if (records.length === 0) return

          await ensureFreshToken(dispatch)

          for (const record of records) {
            const nextAttemptTime = record.nextAttemptAt
              ? new Date(record.nextAttemptAt).getTime()
              : 0
            if (nextAttemptTime > Date.now()) {
              retryDelayToSchedule = nextAttemptTime - Date.now()
              break
            }

            try {
              if (record.mutation.endpoint === 'sendChangeDriverStatus') {
                await dispatch(
                  operationApi.endpoints.sendChangeDriverStatus.initiate(record.mutation.args)
                ).unwrap()
              } else {
                await dispatch(
                  operationApi.endpoints.sendOperations.initiate(record.mutation.args)
                ).unwrap()
              }
              await removeQueuedMutation(record.id)
            } catch (error) {
              if (isPermanentMutationError(error)) {
                await removeQueuedMutation(record.id)
                toast.error(t('syncRejected'))
                continue
              }

              const retryDelay = getRetryDelay(record.attempts)
              await scheduleQueuedMutationRetry(
                record,
                new Date(Date.now() + retryDelay).toISOString()
              )
              retryDelayToSchedule = retryDelay
              break
            }
          }
        })
      } catch {
        retryDelayToSchedule = RETRY_BASE_DELAY_MS
      } finally {
        draining = false
        if (retryDelayToSchedule !== undefined) {
          window.clearTimeout(retryTimer)
          retryTimer = window.setTimeout(drain, retryDelayToSchedule)
        }
      }
    }

    drain()
    window.addEventListener('online', drain)
    window.addEventListener(MUTATION_QUEUE_CHANGED_EVENT, drain)
    return () => {
      window.clearTimeout(retryTimer)
      window.removeEventListener('online', drain)
      window.removeEventListener(MUTATION_QUEUE_CHANGED_EVENT, drain)
    }
  }, [dispatch, ownerId, t])
}

export default useMutationQueueSync
